import {EffectRef, Injectable, Signal, WritableSignal, effect, inject, signal, untracked} from '@angular/core';
import {io, Socket} from "socket.io-client";
import {ApiService} from "./api.service";
import {BehaviorSubject, Subject} from "rxjs";
import {TYPE} from "../types/types";
import {environment} from '../../../environments/environment';
import {CalendarStore} from '../../modules/calendar/stores/calendar-store';
import {TokenService} from './token.service';
import {MaintenanceService} from './maintenance.service';
import {ChatStore} from '../../modules/chat/chat-store/chat-store';
import {EnvironmentService} from './environment.service';
import {NotificationStateService} from './notification-state.service';
import {ProfileService} from './profile.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private apiService: ApiService = inject(ApiService);
  private calendarStore: InstanceType<typeof CalendarStore> = inject(CalendarStore);
  private chatStore: InstanceType<typeof ChatStore> = inject(ChatStore);
  private tokenService: TokenService = inject(TokenService);
  private maintenanceService: MaintenanceService = inject(MaintenanceService);
  private environmentService: EnvironmentService = inject(EnvironmentService);
  private notificationStateService: NotificationStateService = inject(NotificationStateService);
  private profileService: ProfileService = inject(ProfileService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public data: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public reminder: Subject<TYPE.ReminderUpdate> = new Subject<TYPE.ReminderUpdate>();
  
  private _connectionStatus: WritableSignal<'online' | 'reconnecting' | 'offline'> = signal<'online' | 'reconnecting' | 'offline'>('offline');
  public connectionStatus: Signal<'online' | 'reconnecting' | 'offline'> = this._connectionStatus.asReadonly();
  
  private userSocket: WritableSignal<Socket | null> = signal<Socket | null>(null);
  private userId: WritableSignal<number> = signal<number>(0);
  
  private calendarSockets: WritableSignal<Map<string, Socket>> = signal<Map<string, Socket>>(new Map());
  private systemAnnouncementSocket: WritableSignal<Socket | null> = signal<Socket | null>(null);

  public constructor() {
    this.createSystemAnnouncementSocket();
  }

  private socketDataEffect: EffectRef = effect(() => {
    const socket = this.userSocket();
    if(socket) {
      untracked(() => {  
          socket.on('notification.create', (data: {id: number, socket?: string}) => {
            if (!data?.id) {
              return;
            }
            if ((data?.socket ?? '') !== this.environmentService.getTabId()) {
              this.notificationStateService.handleSocketNotificationCreate({id: data.id});
            }
          }); 
          
          socket.on('notification.update', (data?: {id: number, socket?: string}) => {
            if ((data?.socket ?? '') !== this.environmentService.getTabId()) {
              if (data?.id) {
                this.notificationStateService.handleSocketNotificationUpdate({id: data.id});
              } else {
                this.notificationStateService.handleSocketNotificationUpdate();
              }
            }
          });
          
          socket.on('chat.delete', (data: {id: number, socket?: string}) => {
            if((data?.socket ?? '') !== this.environmentService.getTabId()) {
              this.chatStore.handleChatDelete({chat_id: data.id});
            }
          });
          
          socket.on('chat.leave', (data: {id: number, user_id: number, socket?: string}) => {
            if((data?.socket ?? '') !== this.environmentService.getTabId()) {
              this.chatStore.handleChatLeave({chat_id: data.id, user_id: data.user_id});
            }
          });
          
          socket.on('chat.message.update', (data: {id: number, chat_id: number, was_created: boolean, created_at: string, socket?: string}) => {
            if((data?.socket ?? '') !== this.environmentService.getTabId()) {
              this.chatStore.handleMessageUpdate(data);
            }
          });

          socket.on('chat.update', (data: {id: number, was_created: boolean, socket?: string}) => {
            if((data?.socket ?? '') !== this.environmentService.getTabId()) {
              this.chatStore.handleChatUpdate(data);
            }
          }); 
      });
    }
  });

  private calendarSocketsEffect: EffectRef = effect(() => {
    const calendars = this.calendarStore.calendars();
    untracked(() => {
      const calendarIdList = calendars.map(c => `calendar-${c.id}`);

      // close unused sockets
      const currentSockets = this.calendarSockets();
      currentSockets.forEach((socket, roomId) => {
        if(!calendarIdList.includes(roomId)) {
          socket.disconnect();
          currentSockets.delete(roomId);
        }
      });
      this.calendarSockets.set(currentSockets);

      // open new sockets
      calendarIdList.forEach(roomId => {
        if(!currentSockets.has(roomId)) {
          const socket = io(this.apiService.getUrl() + ':6001/' + roomId, {
            reconnectionDelay: 10000,
            reconnection: environment.production,
            transports: ['websocket'], // Disable long polling
            auth: {
              token: this.tokenService.getToken()
            }
          });
          currentSockets.set(roomId, socket);
          this.socketStatusAttacher(roomId, socket);
          socket.on('calendar.event.update', (data: {etag: string, id: number, socket: null, was_created: boolean}) => {
            this.log(`📅 on calendar.event.update received for ${roomId}: ` + JSON.stringify(data));
            const calendarId = parseInt(roomId.split('-')[1], 10);
            this.calendarStore.checkForOutdatedEvent(calendarId, data.id, data.etag, data.was_created);
          });
          socket.on('calendar.event.delete', (data: {id: number, socket: null}) => {
            this.log(`📅 on calendar.event.delete received for ${roomId}: ` + JSON.stringify(data));
            const calendarId = parseInt(roomId.split('-')[1], 10);
            this.calendarStore.checkForEventToDelete(calendarId, data.id);
          });
          // TODO: This event is for further updates of the calendar, when users can have multiple calendars and create, edit and delete them
          // socket.on('calendar.update', (data) => {
          //   this.log(`📅 on calendar.update received for ${roomId}, refreshing calendar: ` + JSON.stringify(data));
          // });
        }
      });
      this.calendarSockets.set(currentSockets);
    });
  });

  private userSocketEffect: EffectRef = effect(() => {
    const newUserId = this.profileService.profile()?.user.id;
    if(newUserId) {
      // Only connect if we don't have a socket or the user ID changed
      if(!this.userSocket() || this.userId() !== newUserId) {
        this.connectUserSocket(newUserId);
      }
    } else {
      // Only disconnect if we have a socket (prevent infinite loop)
      if(this.userSocket() || this.userId() !== 0) {
        this.disconnect();
      }
    }
  });

  private createSystemAnnouncementSocket(): void {
    if (this.systemAnnouncementSocket()) {
      this.systemAnnouncementSocket()!.disconnect();
      this.systemAnnouncementSocket.set(null);
    }

    this.systemAnnouncementSocket.set(io(this.apiService.getUrl() + ':6001/announcement', {
      reconnectionDelay: 10000,
      reconnection: environment.production,
      transports: ['websocket'], // Disable long polling
      auth: {
        token: this.tokenService.getToken()
      }
    }));
    this.socketStatusAttacher('announcement', this.systemAnnouncementSocket()!);
    this.systemAnnouncementSocket()!.on('announcement.update', () => {
      this.log('📢 on announcement.update received, refreshing maintenance info');
      this.maintenanceService.runMaintenanceCheck();
    });
  }

  private connectUserSocket(userId: number): void {
    this.userId.set(userId);
    if (this.userSocket()) {
      this.userSocket()!.disconnect();
      this.userSocket.set(null);
    }

    this.userSocket.set(io(this.apiService.getUrl() + ':6001/user-' + userId, {
      reconnectionDelay: 10000,
      reconnection: environment.production,
      transports: ['websocket'], // Disable long polling
      auth: {
        token: this.tokenService.getToken()
      }
    }));
    this.socketStatusAttacher('user-' + userId, this.userSocket()!);
  }

  private disconnect(): void {
    this.userSocket()?.disconnect();
    this.userSocket.set(null);
    this.userId.set(0);
    this.calendarSockets().forEach(socket => socket.disconnect());
    this.calendarSockets.set(new Map());
    this.log('👋 Disconnected from all sockets');
    this._connectionStatus.set('offline');
  }

  private socketStatusAttacher(name: string, socket: Socket): void {
    if(socket) {
      socket.on('connect', () => {
        this.log(`✅ on connect received for ${name}`);
        if (this.userId() !== 0) {
          this._connectionStatus.set('online');
        }
      });
      socket.on('connect_error', (err: Error) => {
        this.log(`❗️ on connect_error received for ${name}`);
        if(socket.active){
          this.log(`⚠️ temporary connection error for ${name}, automatic reconnecting...`);
          this._connectionStatus.set('reconnecting');
        } else {
          this.error(`❌ connection denied by server for ${name}, message: ${err.message}`);
          this._connectionStatus.set('offline');

        }
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      socket.on('disconnect', (reason: string, details: any) => {
        this.log(`❗️ on disconnect received for ${name}`);
        if(socket.active){
          this.log(`⚠️ disconnected from server for ${name}, automatic reconnecting... reason: ${reason}, details: ${JSON.stringify(details)}`);
          this._connectionStatus.set('reconnecting');
        } else {
          this.error(`❌ disconnected from server for ${name}, reason: ${reason}, details: ${JSON.stringify(details)}`);
          this._connectionStatus.set('offline');
        }
      });
    }
  }

  private log(message: string): void {
    if(environment.writeSocketDebugLogs) {
      console.log('↔️ Socket - ' + message);
    }
  }

  private error(message: string): void {
    console.error('↔️ Socket - ' + message);
  }
}
